'use client';

interface ComparisonData {
  vendedor_id: number;
  vendedor_nome: string;
  vendedores_quantidade: number;
  vendedores_valor: number;
  vendedores_media: number;
  vendedores_desvio_padrao: number;
  tickets_quantidade: number;
  tickets_tempo_medio_horas: number;
  tickets_espera_media_minutos: number;
  tickets_transferencias_media: number;
  tickets_mensagens_media_minutos: number;
  relacao_espera_venda: number;
}

interface GroupMetrics {
  total_vendedores: number;
  media_quantidade_vendas: number;
  media_valor_vendas: number;
  media_tickets: number;
  media_espera_minutos: number;
}

interface ComparisonTableProps {
  comparisons: ComparisonData[];
  groupMetrics: GroupMetrics;
}

export function ComparisonTable({ comparisons, groupMetrics }: ComparisonTableProps) {
  const formatCurrency = (value: number) => {
    return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number, decimals = 2) => {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  return (
    <div className="space-y-8">
      {/* Main Comparison Table */}
      <div className="rounded-lg border overflow-hidden" style={{ borderColor: 'var(--card-border-bright)' }}>
        <div
          className="overflow-x-auto"
          style={{ backgroundColor: 'var(--card-bg)', backdropFilter: 'blur(10px)' }}
        >
          <table className="w-full text-sm" style={{
            padding: '0.5em 1em'
          }}>
            <thead>
              <tr style={{ borderBottomColor: 'var(--card-border-bright)', borderBottomWidth: '1px' }}>
                <th className="px-4 py-3 text-left text-slate-300 font-semibold" style={{padding: '0.5em'}}>Vendedor</th>
                <th className="px-4 py-3 text-center text-slate-300 font-semibold" style={{padding: '0.5em'}}>Qtd Vendas</th>
                <th className="px-4 py-3 text-right text-slate-300 font-semibold" style={{padding: '0.5em'}}>Valor Total</th>
                <th className="px-4 py-3 text-right text-slate-300 font-semibold" style={{padding: '0.5em'}}>Média/Venda</th>
                <th className="px-4 py-3 text-right text-slate-300 font-semibold" style={{padding: '0.5em'}}>Desvio Padrão</th>
                <th className="px-4 py-3 text-center text-slate-300 font-semibold" style={{padding: '0.5em'}}>Qtd Tickets</th>
                <th className="px-4 py-3 text-center text-slate-300 font-semibold" style={{padding: '0.5em'}}>Tempo Médio (h)</th>
                <th className="px-4 py-3 text-center text-slate-300 font-semibold" style={{padding: '0.5em'}}>Espera Média (min)</th>
                <th className="px-4 py-3 text-center text-slate-300 font-semibold" style={{padding: '0.5em'}}>Transf. Média</th>
                <th className="px-4 py-3 text-right text-slate-300 font-semibold" style={{padding: '0.5em'}}>Venda/Hora Espera</th>
              </tr>
            </thead>
            <tbody>
              {comparisons.map((comp, idx) => (
                <tr
                  key={comp.vendedor_id}
                  style={{
                    borderBottomColor: 'var(--card-border)',
                    borderBottomWidth: '1px',
                    backgroundColor: idx % 2 === 0 ? 'rgba(148, 163, 184, 0.05)' : 'transparent',
                  }}
                >
                  <td className="px-4 py-3 text-slate-200 font-medium"  style={{padding: '0.5em'}}>{comp.vendedor_nome}</td>
                  <td className="px-4 py-3 text-center text-slate-300"  style={{padding: '0.5em'}}>{comp.vendedores_quantidade}</td>
                  <td className="px-4 py-3 text-right text-slate-300"  style={{padding: '0.5em'}}>{formatCurrency(comp.vendedores_valor)}</td>
                  <td className="px-4 py-3 text-right text-slate-300"  style={{padding: '0.5em'}}>{formatCurrency(comp.vendedores_media)}</td>
                  <td className="px-4 py-3 text-right text-slate-300"  style={{padding: '0.5em'}}>{formatCurrency(comp.vendedores_desvio_padrao)}</td>
                  <td className="px-4 py-3 text-center text-slate-300"  style={{padding: '0.5em'}}>{comp.tickets_quantidade}</td>
                  <td className="px-4 py-3 text-center text-slate-300"  style={{padding: '0.5em'}}>{formatNumber(comp.tickets_tempo_medio_horas)}</td>
                  <td className="px-4 py-3 text-center text-slate-300"  style={{padding: '0.5em'}}>{formatNumber(comp.tickets_espera_media_minutos)}</td>
                  <td className="px-4 py-3 text-center text-slate-300"  style={{padding: '0.5em'}}>{formatNumber(comp.tickets_transferencias_media)}</td>
                  <td className="px-4 py-3 text-right text-slate-300"  style={{padding: '0.5em'}}>{formatCurrency(comp.relacao_espera_venda)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Group Metrics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4" style={{
        marginTop: '1em',
        marginBottom: '1em'
      }}>
        {[
          { label: 'Total de Vendedores', value: groupMetrics.total_vendedores, format: 'number' },
          { label: 'Média Qtd Vendas', value: groupMetrics.media_quantidade_vendas, format: 'number' },
          { label: 'Média Valor Vendas', value: groupMetrics.media_valor_vendas, format: 'currency' },
          { label: 'Média Tickets', value: groupMetrics.media_tickets, format: 'number' },
          { label: 'Média Espera (min)', value: groupMetrics.media_espera_minutos, format: 'number' },
        ].map((metric) => (
          <div
            key={metric.label}
            className="rounded-lg p-4 border"
            style={{
              backgroundColor: 'var(--card-bg)',
              borderColor: 'var(--card-border-bright)',
              backdropFilter: 'blur(10px)', padding: '0.5em 1.0em',
            }}
          >
            <p className="text-xs text-slate-400 mb-1 font-medium">{metric.label}</p>
            <p className="text-2xl font-bold text-slate-200">
              {metric.format === 'currency'
                ? formatCurrency(metric.value)
                : formatNumber(metric.value)}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
